/**
 * Internal linking map — the crawl-budget lever.
 *
 * Google discovers and re-crawls pages through internal links far faster than
 * through a sitemap alone. Our highest-impression pages (/bosses, /map,
 * /booster-pack, /damage, /guides) already get crawled often, so each of them
 * carries a block of contextual links pointing at pages that are indexed
 * slowly or not at all (deep guides, tools, the AFK gold landing page).
 *
 * Rules for entries:
 *  - anchor text must contain the target page's own head keyword (an anchor
 *    mismatch weakens the signal — see the anchor checker in the admin console)
 *  - only real, existing routes, so the block never emits a soft-404 link
 *  - 4-6 links per block: enough equity per link to matter
 */
export interface InternalLink {
  /** Absolute site path, e.g. "/guides/farming". */
  href: string;
  /** Anchor text — must echo the target's primary keyword. */
  label: string;
  /** One-line reason to click, rendered under the anchor. */
  blurb: string;
}

export interface LinkBlock {
  heading: string;
  intro: string;
  links: InternalLink[];
}

const AFK_GOLD: InternalLink = {
  href: "/afk-gold",
  label: "Palworld AFK gold farm",
  blurb: "The overnight gold setup: base layout, merchant loop and gold per hour.",
};

const MERCH: InternalLink = {
  href: "/merch",
  label: "Palworld merch hub",
  blurb: "Official plush, apparel, figures and TCG accessories, with licensee checks.",
};

const COLLECTING: InternalLink = {
  href: "/guides/palworld-collecting-guide",
  label: "Palworld collecting guide",
  blurb: "Set checklists by printed card code, sleeves, binders and condition grading.",
};

const INVESTING: InternalLink = {
  href: "/guides/palworld-tcg-investing-guide",
  label: "Palworld TCG investing guide",
  blurb: "Which printings carry scarcity, when grading pays, and how to read the price hub.",
};

const FARMING: InternalLink = {
  href: "/guides/farming",
  label: "Palworld farming guide",
  blurb: "Ore, cake, elixir and gold routes with the exact worker suitability levels.",
};

const WIKI: InternalLink = {
  href: "/palworld-wiki",
  label: "Palworld wiki: Pals, maps, breeding & cards",
  blurb: "Every database, calculator and map section in one directory.",
};

const BREEDING: InternalLink = {
  href: "/breeding",
  label: "Palworld breeding calculator",
  blurb: "Parent combos with real breed-power math and passive inheritance odds.",
};

const CAPTURE: InternalLink = {
  href: "/capture-rates",
  label: "Palworld capture rate table",
  blurb: "Sphere-by-sphere catch chances for every Pal, alpha and raid boss.",
};

const MOUNTS: InternalLink = {
  href: "/mounts",
  label: "Palworld mount speeds ranked: fly, ground & swim",
  blurb: "Ranked ride, glide and sprint speeds for every mountable Pal.",
};

const TIER: InternalLink = {
  href: "/tier-list",
  label: "Palworld tier list",
  blurb: "Combat, work and mount tiers ranked from the current 1.0 stat tables.",
};

const DAMAGE: InternalLink = {
  href: "/damage",
  label: "Palworld damage calculator",
  blurb: "Effective DPS, element multipliers and boss time-to-kill per Pal.",
};

const ELEMENTS: InternalLink = {
  href: "/elements",
  label: "Palworld element weakness chart",
  blurb: "Every attack and defence multiplier in one grid.",
};

const BOSSES: InternalLink = {
  href: "/bosses",
  label: "Palworld boss map and weaknesses",
  blurb: "Alpha locations by level, tower and raid HP, counters and drops.",
};

const EGGS: InternalLink = {
  href: "/eggs",
  label: "Palworld egg hatch times",
  blurb: "Every egg size and biome with hatch timers and possible Pals.",
};

const PRICES: InternalLink = {
  href: "/prices",
  label: "Palworld TCG card prices",
  blurb: "Daily sold and listed prices across 16 marketplaces.",
};

const PRICE_INDEX: InternalLink = {
  href: "/tcg-price-index",
  label: "Palworld TCG price index",
  blurb: "The market benchmark level, plus set and rarity sub-indices.",
};

const CARDS: InternalLink = {
  href: "/cards",
  label: "Palworld TCG card rarity and value list",
  blurb: "Every printed English card code with official scans.",
};

const TCG_RULES: InternalLink = {
  href: "/guides/palworld-tcg-gameplay-rules",
  label: "Palworld TCG gameplay rules",
  blurb: "Turn structure, Soul cards, counters and deck legality explained.",
};

const PACK: InternalLink = {
  href: "/booster-pack",
  label: "Palworld booster pack pull rates",
  blurb: "All 7 slots and the exact Dawn of Palpagos odds.",
};

const MAP: InternalLink = {
  href: "/map",
  label: "Palworld interactive map",
  blurb: "Fast travel, alpha spawns, merchants and dungeons with filters.",
};

const DECK: InternalLink = {
  href: "/deck-builder",
  label: "Palworld TCG deck builder",
  blurb: "Build, validate and share a 50-card deck in the browser.",
};

const ARCADE: InternalLink = {
  href: "/palworld-games",
  label: "free Palworld games",
  blurb: "Seven in-browser Palworld games: fusion lab, silhouette quiz, type duel, bracket and more.",
};

const FUSION: InternalLink = {
  href: "/fusion-lab",
  label: "Palworld Pal fusion generator",
  blurb: "Fuse any two Pals into a hybrid with blended stats and a shareable link.",
};

const TYPE_DUEL: InternalLink = {
  href: "/type-duel",
  label: "Palworld type chart quiz",
  blurb: "Sixty seconds of element matchups to memorise every weakness.",
};

const PRICE_QUIZ: InternalLink = {
  href: "/price-guess",
  label: "Palworld card price quiz",
  blurb: "Higher or lower with real printed card codes and index values.",
};

const PAL_CATCH: InternalLink = {
  href: "/pal-catch",
  label: "Palworld catching game",
  blurb: "Animated Pal Sphere Toss: catch drifting wild Pals against a 45-second clock.",
};

const CARD_MATCH: InternalLink = {
  href: "/card-match",
  label: "Palworld card memory match",
  blurb: "Flip real official card scans two at a time and clear all eight pairs.",
};

const CAKE_BAKE: InternalLink = {
  href: "/cake-bake",
  label: "Palworld cake baking game",
  blurb: "Bake cakes with Sekhmet: gather ingredients, stir the batter and time the oven.",
};

const BUILD_SIM: InternalLink = {
  href: "/build-sim",
  label: "Palworld base building game",
  blurb: "Raise blueprints block by block with Cattiva hauling wood, stone and ore.",
};

export const LINK_BLOCKS: Record<string, LinkBlock> = {
  "/palworld-games": {
    heading: "Databases behind the games",
    intro: "Every arcade game reads the same Pal Stars data — study the source and you score higher.",
    links: [ELEMENTS, PRICES, TIER, CARDS, BREEDING, PAL_CATCH, CARD_MATCH, CAKE_BAKE, BUILD_SIM],
  },
  "/bosses": {
    heading: "Beat the boss faster",
    intro: "Pair the boss list with the tools that decide the fight — element matchups, DPS and catch odds.",
    links: [DAMAGE, ELEMENTS, CAPTURE, MOUNTS, AFK_GOLD, FUSION],
  },
  "/map": {
    heading: "Plan the route before you fly",
    intro: "The map answers where; these pages answer what to bring and what to farm when you land.",
    links: [FARMING, AFK_GOLD, EGGS, BOSSES, MOUNTS],
  },
  "/booster-pack": {
    heading: "More Palworld card game tools",
    intro: "Once the pack is open, price it, sleeve it into a deck and check the rules.",
    links: [PRICES, CARDS, DECK, TCG_RULES, PRICE_QUIZ, CARD_MATCH],
  },
  "/damage": {
    heading: "Turn damage numbers into wins",
    intro: "DPS is one input — matchups, mounts and breeding for passives decide the clear.",
    links: [ELEMENTS, BOSSES, BREEDING, TIER, TYPE_DUEL, PAL_CATCH],
  },
  "/guides": {
    heading: "Start with the pages players use most",
    intro: "The fastest routes into the database: calculators, charts and the gold farm.",
    links: [WIKI, AFK_GOLD, FARMING, BREEDING, TIER, DAMAGE, ARCADE],
  },
  "/eggs": {
    heading: "After the egg hatches",
    intro: "Breed the passives you want, then rank the hatch against the current meta.",
    links: [BREEDING, TIER, CAPTURE, FARMING],
  },
  "/afk-gold": {
    heading: "Keep the gold loop running",
    intro: "The farm feeds these next steps — breeding costs, merchant stock and boss gear.",
    links: [FARMING, BREEDING, MAP, BOSSES, PRICES],
  },
  "/prices": {
    heading: "More card market tools",
    intro: "Price context, official scans and the pack odds behind every print run.",
    links: [PRICE_INDEX, CARDS, PACK, DECK, TCG_RULES, INVESTING],
  },
  "/tcg-price-index": {
    heading: "Price a single Palworld card next",
    intro: "The index gives you market direction; these pages price the exact card in your hand.",
    links: [PRICES, CARDS, INVESTING, COLLECTING, PACK, DECK],
  },
  "/cards": {
    heading: "Use the card database with these tools",
    intro: "Once you have the printed card number, price it, sleeve it into a deck and check the rules it plays by.",
    links: [PRICES, DECK, PACK, TCG_RULES, COLLECTING, MERCH],
  },
  "/deck-builder": {
    heading: "Build a stronger Palworld TCG deck",
    intro: "The builder validates the list; these pages decide which cards belong in it.",
    links: [CARDS, TCG_RULES, PRICES, PACK],
  },
  "/tcg-tools": {
    heading: "More Palworld card game tools",
    intro: "Verify a card, price it, then take the list into the deck builder.",
    links: [CARDS, PRICES, DECK, PACK],
  },
  "/merch": {
    heading: "Collect and value what you buy",
    intro: "Merch buying guides pair with the card database, the price hub and the collecting playbook.",
    links: [CARDS, PRICES, COLLECTING, INVESTING],
  },
  // The wiki hub is the generic "palworld wiki" entry point: it exists to push
  // crawl equity straight into the exact-match tool pages.
  "/palworld-wiki": {
    heading: "Most-used Palworld wiki pages",
    intro: "The pages players open first when they land on the wiki.",
    links: [BREEDING, MAP, ELEMENTS, TIER, CAPTURE, CARDS],
  },
};

/** The link block for a path, if one is configured. */
export function linkBlockFor(path: string): LinkBlock | undefined {
  return LINK_BLOCKS[path.replace(/\/+$/, "") || "/"];
}

/** Every (source → target) pair, for the anchor-mismatch audit. */
export function linkPairs(): Array<{ from: string; to: string; anchor: string }> {
  return Object.entries(LINK_BLOCKS).flatMap(([from, block]) =>
    block.links.map((l) => ({ from, to: l.href, anchor: l.label })),
  );
}
